import React, { createContext, useContext, useRef, useState } from 'react';
import { BunkeringPageTabs } from 'src/components/bunkering/BunkeringUtils';
import { ContextProps } from 'src/types/ContextProps';
import { Bunkering } from 'src/types/ViewModel/Bunkering/Bunkering';
import { BunkeringEvent } from 'src/types/ViewModel/Bunkering/BunkeringEvent';
import { BunkerLoss } from 'src/types/ViewModel/Bunkering/BunkerLoss';
import { FuelROB } from 'src/types/ViewModel/Bunkering/FuelROB';

interface BunkerContextProps {
    selectedBunkeringDate: Date | null;
    setSelectedBunkeringDate: (date: Date | null) => void;
    selectedBunkeringTab: BunkeringPageTabs;
    setSelectedBunkeringTab: (tab: BunkeringPageTabs) => void;
    bunkeringEvents: BunkeringEvent[];
    setBunkeringEvents: (bunkeringEvents: BunkeringEvent[]) => void;
    bunkerings: Bunkering[];
    setBunkerings: (bunkerings: Bunkering[]) => void;
    fuelRobData: FuelROB[];
    setFuelRobData: (fuelRobData: FuelROB[]) => void;
    saveBunkeringPageData: boolean;
    setSaveBunkeringPageData: (saveBunkeringPageData: boolean) => void;
    selectedBunkeringEventId: string | null;
    setSelectedBunkeringEventId: (id: string | null) => void;
    selectedBunkeringEvent: BunkeringEvent | null;
    setSelectedBunkeringEvent: (bunkeringEvent: BunkeringEvent | null) => void;
    selectedBunkering: Bunkering | null;
    setSelectedBunkering: (bunkering: Bunkering | null) => void;
    bunkerLossData: BunkerLoss[] | null;
    setBunkerLossData: (bunkerLossData: BunkerLoss[]) => void;
    saveButtonEnabled: boolean;
    setSaveButtonEnabled: (enabled: boolean) => void;
    deletedBunkerings: Bunkering[];
    setDeletedBunkerings: (deletedBunkerings: Bunkering[]) => void;
    isSaving: boolean;
    setIsSaving: (isSaving: boolean) => void;
    saveSaveIdRef: React.MutableRefObject<string | null>;
    setSaveSaveId: (id: string | null) => void;
}

export const BunkerContext = createContext<BunkerContextProps>(undefined!);

export const BunkerProvider = ({ children }: ContextProps) => {
    const [selectedBunkeringDate, setSelectedBunkeringDate] = useState<Date | null>(null);
    const [selectedBunkeringTab, setSelectedBunkeringTab] = useState<BunkeringPageTabs>(BunkeringPageTabs.rob);
    const [bunkeringEvents, setBunkeringEvents] = useState<BunkeringEvent[]>([]);
    const [bunkerings, setBunkerings] = useState<Bunkering[]>([]);
    const [fuelRobData, setFuelRobData] = useState<FuelROB[]>([]);
    const [saveBunkeringPageData, setSaveBunkeringPageData] = useState<boolean>(false);
    const [selectedBunkeringEventId, setSelectedBunkeringEventId] = useState<string | null>(null);
    const [selectedBunkeringEvent, setSelectedBunkeringEvent] = useState<BunkeringEvent | null>(null);
    const [selectedBunkering, setSelectedBunkering] = useState<Bunkering>(null);
    const [bunkerLossData, setBunkerLossData] = useState<BunkerLoss[]>([]);
    const [saveButtonEnabled, setSaveButtonEnabled] = useState<boolean>(false);
    const [deletedBunkerings, setDeletedBunkerings] = useState<Bunkering[]>([]);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const saveSaveIdRef = useRef<string | null>(null);
    const setSaveSaveId = (id: string | null) => { saveSaveIdRef.current = id; };

    return (
        <BunkerContext.Provider
            value={{
                selectedBunkeringDate,
                setSelectedBunkeringDate,
                selectedBunkeringTab,
                setSelectedBunkeringTab,
                bunkeringEvents,
                setBunkeringEvents,
                bunkerings,
                setBunkerings,
                fuelRobData,
                setFuelRobData,
                saveBunkeringPageData,
                setSaveBunkeringPageData,
                selectedBunkeringEventId,
                setSelectedBunkeringEventId,
                selectedBunkeringEvent,
                setSelectedBunkeringEvent,
                selectedBunkering,
                setSelectedBunkering,
                bunkerLossData,
                setBunkerLossData,
                saveButtonEnabled,
                setSaveButtonEnabled,
                deletedBunkerings,
                setDeletedBunkerings,
                isSaving,
                setIsSaving,
                saveSaveIdRef,
                setSaveSaveId,
            }}
        >
            {children}
        </BunkerContext.Provider>
    );
};

export const useBunkerContext = () => useContext(BunkerContext);
